import './Confirmation.scss'

import { useState } from 'react'
import parse from 'html-react-parser'
import Button from 'react-bootstrap/Button'

import BlobAnimation from 'components/practices/BlobAnimation'
import CourseRating from 'components/courses/CourseRating/CourseRating'
import ConfirmationDrawer from './ConfirmationDrawer'
import { isAndroid, isiOS } from 'components/common/helpers/Mobile'
import useConfetti from 'components/common/hooks/useConfetti'

/**
 * Confirmation component. Used for displaying a confirmation message and directing a user to a link.
 * @param {Object} params Initial props:
 * `children` - Child elements to render, if any
 * `title` - Title of the confirmation
 * `subtitle` - Subtitle of the confirmation
 * `message` - Message of the confirmation
 * `btnOnClick` - Function to execute when the button is clicked
 * `btnText` - Text of the button
 * `linkOnClick` - Function to execute when the link is clicked
 * `linkText` - Text of the link
 * `showConfetti` - Whether to show confetti (default: false)
 */
export default function Confirmation({
  children,
  title,
  subtitle,
  message,
  btnOnClick,
  btnText,
  linkOnClick,
  linkText,
  showConfetti = false,
  lesson,
  fullCourse,
  courseProgress,
  closingStatement
}) {
  const buttonProps = {
    btnText,
    btnOnClick
  }

  const [isVisible, setIsVisible] = useState(false)

  // Use confetti if showConfetti is true
  useConfetti(showConfetti)

  const handleDrawerVisible = visible => {
    setIsVisible(visible)
  }

  return (
    <>
      <div className='confirmation-wrapper'>
        <BlobAnimation />
        <div className='confirmation-container'>
          <div className='confirmation-text'>
            <h3 className='title'>{title}</h3>
            {subtitle && <h4 className='subtitle'>{subtitle}</h4>}
            {message && closingStatement ? (
              <div className='mb-2 mb-md-4'>{`${parse(
                message
              )} ${closingStatement}`}</div>
            ) : (
              <div className='mt-4'>{message && parse(message)}</div>
            )}
            {children}
            {btnOnClick && btnText && (
              <Button
                className='w-100 my-4'
                variant='primary'
                onClick={btnOnClick}
              >
                {btnText}
              </Button>
            )}
            {linkOnClick && linkText && (
              <span className='action-link' onClick={linkOnClick}>
                {linkText}
              </span>
            )}
          </div>
          {lesson && (
            <>
              <CourseRating lesson={lesson} buttonProps={buttonProps} />

              {/* Only show reminders for mobile app users */}
              {(isiOS('1.0.17') || isAndroid()) && (
                <Button
                  className='w-75 mt-4 reminder-button'
                  onClick={() => setIsVisible(true)}
                >
                  Turn on Reminders
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      {lesson && (
        <ConfirmationDrawer
          fullCourse={fullCourse}
          courseProgress={courseProgress}
          visible={isVisible}
          handleDrawerVisible={handleDrawerVisible}
        />
      )}
    </>
  )
}
